import { FC } from "react";
import {
  OakP,
  OakHeading,
  OakLI,
  OakTypography,
  OakFlex,
  OakBox,
  OakTertiaryOLNav,
  OakSecondaryLink,
  OakSpan,
} from "@oaknational/oak-components";
import {
  PortableText,
  PortableTextBlockComponent,
  PortableTextComponents,
} from "@portabletext/react";
import styled from "styled-components";
import slugify from "slugify";
import { useRouter } from "next/router";

import useAnalytics from "@/context/Analytics/useAnalytics";
import ScreenReaderOnly from "@/components/SharedComponents/ScreenReaderOnly";
import Flex from "@/components/SharedComponents/Flex.deprecated";
import { CurriculumOverviewMVData } from "@/node-lib/curriculum-api-2023";
import { CurriculumOverviewSanityData } from "@/common-lib/cms-types";
import CMSImage from "@/components/SharedComponents/CMSImage";
import CMSVideo from "@/components/SharedComponents/CMSVideo";
import { basePortableTextComponents } from "@/components/SharedComponents/PortableText";
import { findContainingAnchor } from "@/utils/curriculum/dom";
import { CurriculumSelectionSlugs } from "@/utils/curriculum/slugs";
import { PhaseValueType } from "@/browser-lib/avo/Avo";
import { resolveOakHref } from "@/common-lib/urls";

export type OverviewTabProps = {
  data: {
    curriculumInfo: CurriculumOverviewMVData;
    curriculumCMSInfo: CurriculumOverviewSanityData;
    curriculumSelectionSlugs: CurriculumSelectionSlugs;
  };
};

const ExplainerStyles = styled("div")`
  h3:first-child {
    margin-top: 0;
  }
  h3 {
    font-weight: 600;
    margin-top: 1rem;
  }
  h4 {
    font-weight: 600;
    margin-top: 1rem;
  }
  h5 {
    font-weight: 600;
    margin-top: 0.5rem;
  }
  h6 {
    font-weight: 600;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
  }
  ul + h3,
  p + h3 {
    margin-top: 4.5rem;
  }
  ul + h4,
  p + h4 {
    margin-top: 1.5 em;
  }
  ul + h5,
  p + h5 {
    margin-top: 3.5rem;
  }
  ul + h6,
  p + h6 {
    margin-top: 0.5rem;
  }

  h3 + h4 {
    margin-top: 1.5rem;
  }
  h4 + h5 {
    margin-top: 1.5rem;
  }
  h5 + h6 {
    margin-top: 1.5rem;
  }
`;

const slugifyHash = (input: string) => slugify(input).toLocaleLowerCase();

const blockHeadingComponents: PortableTextComponents["block"] = {
  heading1: (props) => (
    <OakHeading
      tag="h3"
      id={`header-${slugifyHash(props.value.children[0]?.text ?? "unknown")}`}
      $font={["heading-5", "heading-5", "heading-4"]}
    >
      {props.children}
    </OakHeading>
  ),
  heading2: (props) => (
    <OakHeading tag="h4" $font={["body-2", "body-2", "heading-6"]}>
      {props.children}
    </OakHeading>
  ),
  heading3: (props) => (
    <OakHeading tag="h5" $font={["body-2"]}>
      {props.children}
    </OakHeading>
  ),
  heading4: (props) => (
    <OakHeading tag="h6" $font={["body-2"]}>
      {props.children}
    </OakHeading>
  ),
};

const markComponents: PortableTextComponents["marks"] = {
  link: ({ children, value }) => {
    return <OakSecondaryLink {...value}>{children}</OakSecondaryLink>;
  },
};

const OverviewTab: FC<OverviewTabProps> = (props: OverviewTabProps) => {
  const router = useRouter();
  const { track } = useAnalytics();

  const { curriculumCMSInfo, curriculumInfo, curriculumSelectionSlugs } =
    props.data;
  const { subjectTitle } = curriculumInfo;
  const { subjectSlug, phaseSlug } = curriculumSelectionSlugs;
  const {
    curriculumExplainer,
    curriculumPartnerOverviews,
    video,
    videoExplainer,
  } = curriculumCMSInfo;
  const isVideoEnabled = video && videoExplainer;

  const partnerTitle = `Our curriculum partner${
    curriculumPartnerOverviews?.length > 1 ? "s" : ""
  }`;

  const h1Headings = (curriculumExplainer.explainerRaw ?? []).filter(
    (block) => {
      return block.style === "heading1";
    },
  );

  const goToAnchor = (selector: string) => {
    Array.from(document.querySelectorAll(selector))
      .find((el: Element) => el.checkVisibility())
      ?.scrollIntoView();
  };

  const navItems = h1Headings.map((heading) => {
    return {
      title: heading.children[0].text,
      href: `#header-${slugifyHash(heading.children[0].text)}`,
    };
  });

  if (isVideoEnabled) {
    navItems.push({
      href: "#header-video-guide",
      title: "Video guide",
    });
  }

  navItems.push({
    href: "#header-our-curriculum-partner",
    title: partnerTitle,
  });

  const handleAnalytics = () => {
    track.curriculumExplainerExplored({
      subjectTitle: subjectTitle,
      subjectSlug: subjectSlug,
      platform: "owa",
      product: "curriculum visualiser",
      engagementIntent: "explore",
      componentType: "explainer_tab",
      eventVersion: "2.0.0",
      analyticsUseCase: "Teacher",
      phase: phaseSlug as PhaseValueType,
    });
  };

  const onClickNavItem = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (e.target instanceof HTMLElement) {
      const anchor = findContainingAnchor(e.target);
      if (anchor) {
        const url = new URL(anchor.href);
        handleAnalytics();
        router.replace(url.hash);
        goToAnchor(url.hash);
      }
    }
  };

  const contents = (
    <OakFlex $gap={"spacing-24"} $flexDirection={"column"}>
      <OakTertiaryOLNav
        items={navItems}
        title={"Contents"}
        onClick={onClickNavItem}
      />
    </OakFlex>
  );

  return (
    <OakBox $mt={["spacing-0", "spacing-0", "spacing-48"]}>
      <OakBox $minWidth={"100%"} $display={["block", "block", "none"]}>
        <OakBox
          $background={"bg-decorative1-very-subdued"}
          $ph="spacing-16"
          $pv="spacing-20"
        >
          {contents}
        </OakBox>
      </OakBox>
      <OakBox
        id="curriculum-overview"
        aria-labelledby="curriculum-overview-heading"
        tabIndex={-1}
        $maxWidth="spacing-1280"
        $mh={"auto"}
        $ph="spacing-16"
        $width={"100%"}
        role="region"
      >
        <OakFlex $gap={"spacing-120"} $alignItems={"flex-start"}>
          <ScreenReaderOnly>
            <OakHeading
              tag="h2"
              data-testid="overview-heading"
              id="curriculum-overview-heading"
            >
              Explainer
            </OakHeading>
          </ScreenReaderOnly>
          <OakBox
            style={{ minWidth: 300 }}
            $position={["static", "static", "sticky"]}
            $top="spacing-20"
            $pb="spacing-40"
            $display={["none", "none", "block"]}
          >
            {contents}
          </OakBox>
          <OakFlex
            $mb="spacing-8"
            $mt={["spacing-24", "spacing-24", "spacing-0"]}
          >
            <OakBox
              $pb={"spacing-48"}
              $mh={["auto", "auto", "spacing-0"]}
              $textAlign={"left"}
            >
              <OakBox data-testid="explainer">
                <ExplainerStyles>
                  <PortableText
                    value={curriculumExplainer.explainerRaw}
                    components={{
                      ...basePortableTextComponents.list,
                      listItem: {
                        bullet: (props) => (
                          <OakLI $font={["list-item-2", "list-item-1"]}>
                            {props.children}
                          </OakLI>
                        ),
                        number: (props) => (
                          <OakLI $font={["list-item-2", "list-item-1"]}>
                            {props.children}
                          </OakLI>
                        ),
                      },
                      block: {
                        ...basePortableTextComponents.block,
                        ...blockHeadingComponents,
                      } as PortableTextBlockComponent,
                      types: {},
                      marks: {
                        strong: basePortableTextComponents.marks!.strong,
                        em: basePortableTextComponents.marks!.em,
                        link: markComponents.link,
                      },
                    }}
                  />
                </ExplainerStyles>
              </OakBox>
            </OakBox>
          </OakFlex>
        </OakFlex>
        {isVideoEnabled && (
          <OakFlex
            data-testid="video-guide"
            $alignItems={"center"}
            $justifyContent={"flex-start"}
            $flexDirection={["column-reverse", "row"]}
            $gap={["spacing-24", "spacing-120"]}
            $mb={["spacing-48", "spacing-80"]}
          >
            <OakBox>
              <CMSVideo video={video} location="lesson" />
            </OakBox>
            {/* @todo replace with OakFlex - work out $maxWidth */}
            <Flex
              $flexDirection={"column"}
              $maxWidth={["100%", "30%"]}
              $alignItems={"flex-start"}
              $gap={[16, 24]}
            >
              <OakHeading
                tag="h3"
                $font={["heading-6", "heading-5"]}
                id="header-video-guide"
              >
                Video guide
              </OakHeading>
              <OakP $font={"body-1"}>{videoExplainer}</OakP>
              <OakSpan $font={"body-2-bold"} $color="black">
                <OakSecondaryLink
                  href={resolveOakHref({
                    page: "blog-single",
                    blogSlug: "our-approach-to-curriculum",
                  })}
                  iconName="chevron-right"
                  isTrailingIcon={true}
                  color="black"
                >
                  Read more about our new curriculum
                </OakSecondaryLink>
              </OakSpan>
            </Flex>
          </OakFlex>
        )}
      </OakBox>
      <OakBox $background={"bg-decorative1-subdued"} $pv="spacing-48">
        <OakBox
          $maxWidth="spacing-1280"
          $mh={"auto"}
          $ph="spacing-20"
          $width={"100%"}
        >
          <OakFlex
            $gap={["spacing-24", "spacing-32"]}
            $flexDirection={"column"}
          >
            <OakBox $display={["block", "none"]}>
              <OakHeading
                tag="h3"
                $font={["heading-5"]}
                id="header-our-curriculum-partner"
              >
                {partnerTitle}
              </OakHeading>
            </OakBox>
            <OakFlex
              $gap={["spacing-48", "spacing-32"]}
              $flexDirection={"column"}
            >
              {curriculumPartnerOverviews.map(
                (
                  { curriculumPartner, partnerBio, partnerBioPortableTextRaw },
                  curriculumPartnerIndex,
                ) => {
                  return (
                    <OakFlex
                      key={`curriculum-partner-${curriculumPartnerIndex}`}
                      data-testid="curriculum-partner"
                      $justifyContent={"center"}
                      $alignContent={"start"}
                      $gap={["spacing-16", "spacing-32"]}
                      $flexDirection={["column", "row"]}
                    >
                      <OakBox
                        $borderRadius="border-radius-s"
                        $overflow={"hidden"}
                        $borderColor="border-neutral-lighter"
                        $borderStyle={"solid"}
                        $maxHeight={["spacing-180", "spacing-240"]}
                        $maxWidth={["spacing-180", "spacing-240"]}
                        $minHeight={["spacing-180", "spacing-240"]}
                        $minWidth={["spacing-180", "spacing-240"]}
                      >
                        <CMSImage
                          image={{
                            ...curriculumPartner.image,
                            altText: `Logo for ${curriculumPartner.name}`,
                          }}
                        />
                      </OakBox>
                      <OakFlex
                        $flexGrow={1}
                        $flexDirection={"column"}
                        $gap={"spacing-16"}
                        $justifyContent={"center"}
                      >
                        {curriculumPartnerIndex === 0 && (
                          <OakBox $display={["none", "block"]}>
                            <OakHeading
                              tag="h3"
                              $font={["heading-5"]}
                              id="header-our-curriculum-partner"
                            >
                              {partnerTitle}
                            </OakHeading>
                          </OakBox>
                        )}
                        <OakHeading tag="h4" $font={["heading-6"]}>
                          {curriculumPartner.name}
                        </OakHeading>
                        <OakTypography $font={"body-1"}>
                          {!partnerBioPortableTextRaw ? (
                            partnerBio
                          ) : (
                            <PortableText
                              value={partnerBioPortableTextRaw}
                              components={{
                                block: basePortableTextComponents.block,
                                types: {},
                                marks: {
                                  strong:
                                    basePortableTextComponents.marks!.strong,
                                  em: basePortableTextComponents.marks!.em,
                                  link: markComponents.link,
                                },
                              }}
                            />
                          )}
                        </OakTypography>
                      </OakFlex>
                    </OakFlex>
                  );
                },
              )}
            </OakFlex>
          </OakFlex>
        </OakBox>
      </OakBox>
    </OakBox>
  );
};
export default OverviewTab;
